import { useContext, useEffect, useRef, useState } from "react";
import { Card } from "flowbite-react";
import { StatusContext } from "../../../context/statusProvider";
import {
  getMerch,
  listenRequestCounts,
} from "../../../services/merchManager/merchManagerActions";
import AcceptedRequests from "../../../layouts/merchManager/acceptedRequests";
import Inventory from "../../../layouts/merchManager/inventory";
import PendingRequests from "../../../layouts/merchManager/pendingRequests";
import AddMerchForm from "./addMerchFrom";
import MerchDetail from "../../../layouts/merchManager/merchDetail";
import PendingRequestDetail from "../../../layouts/merchManager/pendingReqDetail";
import { CustomModal } from "../../../layouts/customModal";
import AcceptedRequestDetail from "../../../layouts/merchManager/acceptedReqDetail";
import {
  EmptyState,
  TabSwitcher,
} from "../../../layouts/merchManager/components";
import DeclinedRequests from "../../../layouts/merchManager/declinedRequests";
import DeclinedRequestDetail from "../../../layouts/merchManager/declinedReqDetail";
import { useParams, useSearchParams } from "react-router-dom";
import AllowedUsers from "../../../routes/allowedUsers";
import { PERMISSIONS } from "../../../constants/permissions";

const MerchendiseEditorSection = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const routeTab = searchParams.get("tab");

  const [tab, setTab] = useState(
    routeTab === "accepted"
      ? "accepted_requests"
      : routeTab === "declined"
        ? "declined"
        : routeTab === "pending"
          ? "requests"
          : "inventory",
  );

  useEffect(() => {
    if (!routeTab) {
      setTab("inventory");
    } else if (routeTab === "accepted") {
      setTab("accepted_requests");
    } else if (routeTab === "declined") {
      setTab("declined");
    } else if (routeTab === "pending") {
      setTab("requests");
    }
  }, [routeTab]);

  const [merch, setMerch] = useState([]);

  const [pendingCount, setPendingCount] = useState(0);
  // const [acceptedCount, setAcceptedCount] = useState(0);

  const [selectedMerch, setSelectedMerch] = useState();
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [isLoadingMerch, setIsLoadingMerch] = useState(true);

  const [isLoadingReq, setIsLoadingReq] = useState(true);

  const { handleApi, updateAPI } = useContext(StatusContext);
  const modalRef = useRef(null);
  const openRef = useRef(null);

  useEffect(() => {
    getMerch({ setIsLoadingMerch }).then((res) => {
      setMerch(res);
      // keep selected in sync
      setSelectedMerch((prev) =>
        prev
          ? (res.find((m) => m.id === prev.id) ?? res[0] ?? null)
          : (res[0] ?? null),
      );
    });
  }, [updateAPI]);

  useEffect(() => {
    const unsubscribe = listenRequestCounts(({ pending, accepted }) => {
      setPendingCount(pending);
      // setAcceptedCount(accepted);
    });

    return () => unsubscribe();
  }, []);
  return (
    <div className="container">
      <div className="max-w-full mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-16">
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 w-full items-start">
          {/* ── LEFT PANEL ─────────────────────────────── */}
          <div className="w-full">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-3xl font-bold tracking-tight text-secondary">
                Merchandise
              </h2>
              <AllowedUsers permission={[PERMISSIONS.MERCH_EDITOR]}>
                <CustomModal
                  btnText="+ New item"
                  modalRef={modalRef}
                  openRef={openRef}
                  isIndex={true}
                  closeOnOutsideClick={true}
                >
                  <div className="w-full sm:w-[480px]">
                    <AddMerchForm handleApi={handleApi} modalRef={modalRef} />
                  </div>
                </CustomModal>
              </AllowedUsers>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 overflow-x-auto">
              <TabSwitcher
                title="Inventory"
                value="inventory"
                activeTab={tab}
                children={
                  <span className="hidden sm:inline-flex text-xs font-normal text-gray-400">
                    {merch.length} items
                  </span>
                }
                onClick={() => {
                  if (tab !== "inventory") {
                    setTab("inventory");
                    setSelectedRequest(null);
                    setSelectedMerch(merch[0] ?? null);
                  }
                }}
              />

              <TabSwitcher
                title="Approved"
                value="accepted_requests"
                activeTab={tab}
                // count={acceptedCount}
                onClick={() => {
                  if (tab !== "accepted_requests") {
                    setTab("accepted_requests");
                    setSelectedRequest(null);
                    setSelectedMerch(null);
                    setIsLoadingReq(true);
                  }
                }}
              />
              <TabSwitcher
                title="Pending"
                value="requests"
                activeTab={tab}
                count={pendingCount}
                onClick={() => {
                  if (tab !== "requests") {
                    setTab("requests");
                    setSelectedMerch(null);
                    setIsLoadingReq(true);
                  }
                }}
              />
              <TabSwitcher
                title="Declined"
                value="declined"
                activeTab={tab}
                onClick={() => {
                  if (tab !== "declined") {
                    setTab("declined");
                    setSelectedMerch(null);
                    setIsLoadingReq(true);
                  }
                }}
              />
            </div>

            {tab === "inventory" && (
              <Inventory
                merch={merch}
                selectedMerch={selectedMerch}
                setSelectedMerch={setSelectedMerch}
                isLoadingMerch={isLoadingMerch}
              />
            )}

            {tab === "accepted_requests" && (
              <AcceptedRequests
                merch={merch}
                updateAPI={updateAPI}
                selectedRequest={selectedRequest}
                setSelectedRequest={setSelectedRequest}
                onLoadingChange={setIsLoadingReq}
                targetRequestId={id}
              />
            )}

            {tab === "requests" && (
              <PendingRequests
                merch={merch}
                updateAPI={updateAPI}
                selectedRequest={selectedRequest}
                setSelectedRequest={setSelectedRequest}
                onLoadingChange={setIsLoadingReq}
                targetRequestId={id}
              />
            )}
            {tab === "declined" && (
              <DeclinedRequests
                merch={merch}
                updateAPI={updateAPI}
                selectedRequest={selectedRequest}
                setSelectedRequest={setSelectedRequest}
                onLoadingChange={setIsLoadingReq}
                targetRequestId={id}
              />
            )}
          </div>

          {/* ── RIGHT PANEL ─────────────────────────────── */}
          <div className="w-full">
            <h2 className="text-3xl font-bold tracking-tight text-secondary mb-5">
              {tab === "inventory" ? "Item Details" : "Request Details"}
            </h2>

            <Card className="min-h-[500px]">
              {tab === "inventory" ? (
                selectedMerch ? (
                  <MerchDetail item={selectedMerch} handleApi={handleApi} />
                ) : (
                  <EmptyState
                    icon={
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M20 13V7a2 2 0 00-2-2h-3V3H9v2H6a2 2 0 00-2 2v6m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4"
                        />
                      </svg>
                    }
                    text="No merchandise items found."
                  />
                )
              ) : tab === "requests" ? (
                isLoadingReq ? null : selectedRequest ? (
                  <PendingRequestDetail
                    request={selectedRequest}
                    merch={merch}
                    handleApi={handleApi}
                  />
                ) : (
                  <EmptyState
                    icon={
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                    text="No pending requests — all caught up!"
                  />
                )
              ) : tab === "accepted_requests" ? (
                isLoadingReq ? null : selectedRequest ? (
                  <AcceptedRequestDetail
                    request={selectedRequest}
                    merch={merch}
                    handleApi={handleApi}
                  />
                ) : (
                  <EmptyState
                    icon={
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                    text="No approved requests yet."
                  />
                )
              ) : tab === "declined" ? (
                isLoadingReq ? null : selectedRequest ? (
                  <DeclinedRequestDetail
                    request={selectedRequest}
                    merch={merch}
                  />
                ) : (
                  <EmptyState
                    icon={
                      <svg
                        className="w-10 h-10"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    }
                    text="No declined requests."
                  />
                )
              ) : null}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchendiseEditorSection;
